import type { StoneColor } from '@wx-weiqi/contracts';

import type { ProjectedByoyomi } from './clock-projection';

const FIVE_MINUTES_MS = 300_000;
const ONE_MINUTE_MS = 60_000;
const THIRTY_SECONDS_MS = 30_000;
const TWENTY_SECONDS_MS = 20_000;
const MILESTONE_LATE_TOLERANCE_MS = 5_000;

interface SideVoiceMemory {
  readonly byoyomiEntered: boolean;
  readonly lowestPeriods: number | null;
  readonly lastMainRemainingMs: number | null;
  readonly fiveMinutesAnnounced: boolean;
  readonly oneMinuteAnnounced: boolean;
  readonly thirtySecondsAnnounced: boolean;
  readonly twentySecondsAnnounced: boolean;
}

export interface ClockVoiceMemory {
  readonly gameId: string | null;
  readonly perspectiveColor: StoneColor | null;
  readonly black: SideVoiceMemory;
  readonly white: SideVoiceMemory;
  readonly activeTurnKey: string | null;
  readonly lastCountdownSecond: number | null;
}

export interface ClockVoiceFrame {
  readonly gameId: string;
  readonly perspectiveColor: StoneColor | null;
  readonly mainTimeMs: number | null;
  readonly byoyomiConfigured: boolean;
  readonly activeColor: StoneColor | null;
  readonly turnStartedAt: string | null;
  readonly blackMainRemainingMs: number | null;
  readonly whiteMainRemainingMs: number | null;
  readonly blackByoyomi: ProjectedByoyomi | null;
  readonly whiteByoyomi: ProjectedByoyomi | null;
  readonly blackPeriods: number | null;
  readonly whitePeriods: number | null;
}

export interface ClockVoiceAdvance {
  readonly memory: ClockVoiceMemory;
  readonly announcements: readonly string[];
}

const EMPTY_SIDE: SideVoiceMemory = Object.freeze({
  byoyomiEntered: false,
  lowestPeriods: null,
  lastMainRemainingMs: null,
  fiveMinutesAnnounced: false,
  oneMinuteAnnounced: false,
  thirtySecondsAnnounced: false,
  twentySecondsAnnounced: false,
});

export function initialClockVoiceMemory(): ClockVoiceMemory {
  return {
    gameId: null,
    perspectiveColor: null,
    black: EMPTY_SIDE,
    white: EMPTY_SIDE,
    activeTurnKey: null,
    lastCountdownSecond: null,
  };
}

export function advanceClockVoice(
  previous: ClockVoiceMemory,
  frame: ClockVoiceFrame,
): ClockVoiceAdvance {
  if (previous.gameId !== frame.gameId || previous.perspectiveColor !== frame.perspectiveColor) {
    return {
      memory: initializeMemory(frame),
      announcements: [],
    };
  }

  const announcements: string[] = [];
  const black =
    frame.perspectiveColor === 'BLACK'
      ? advanceSide(
          previous.black,
          frame.mainTimeMs,
          frame.byoyomiConfigured,
          frame.blackMainRemainingMs,
          frame.blackByoyomi,
          frame.blackPeriods,
          announcements,
        )
      : previous.black;
  const white =
    frame.perspectiveColor === 'WHITE'
      ? advanceSide(
          previous.white,
          frame.mainTimeMs,
          frame.byoyomiConfigured,
          frame.whiteMainRemainingMs,
          frame.whiteByoyomi,
          frame.whitePeriods,
          announcements,
        )
      : previous.white;
  const activeProjection =
    frame.perspectiveColor === null || frame.activeColor !== frame.perspectiveColor
      ? null
      : frame.perspectiveColor === 'BLACK'
        ? frame.blackByoyomi
        : frame.whiteByoyomi;
  const activeTurnKey =
    frame.perspectiveColor === null || frame.activeColor !== frame.perspectiveColor
      ? null
      : `${frame.gameId}:${frame.perspectiveColor}:${frame.turnStartedAt ?? 'unknown'}`;
  const lastCountdownSecond =
    activeTurnKey === previous.activeTurnKey ? previous.lastCountdownSecond : null;
  const activeMainRemainingMs =
    frame.perspectiveColor === null || frame.activeColor !== frame.perspectiveColor
      ? null
      : frame.perspectiveColor === 'BLACK'
        ? frame.blackMainRemainingMs
        : frame.whiteMainRemainingMs;
  const countdownSecond = countdownFor(
    activeProjection,
    frame.byoyomiConfigured ? null : activeMainRemainingMs,
  );

  if (countdownSecond !== null && countdownSecond !== lastCountdownSecond) {
    announcements.push(String(countdownSecond));
  }

  return {
    memory: {
      gameId: frame.gameId,
      perspectiveColor: frame.perspectiveColor,
      black,
      white,
      activeTurnKey,
      lastCountdownSecond: countdownSecond ?? lastCountdownSecond,
    },
    announcements,
  };
}

function initializeMemory(frame: ClockVoiceFrame): ClockVoiceMemory {
  const activeProjection =
    frame.perspectiveColor === null || frame.activeColor !== frame.perspectiveColor
      ? null
      : frame.perspectiveColor === 'BLACK'
        ? frame.blackByoyomi
        : frame.whiteByoyomi;
  const activeMainRemainingMs =
    frame.perspectiveColor === null || frame.activeColor !== frame.perspectiveColor
      ? null
      : frame.perspectiveColor === 'BLACK'
        ? frame.blackMainRemainingMs
        : frame.whiteMainRemainingMs;
  return {
    gameId: frame.gameId,
    perspectiveColor: frame.perspectiveColor,
    black: initializeSide(
      frame.mainTimeMs,
      frame.byoyomiConfigured,
      frame.blackMainRemainingMs,
      frame.blackByoyomi,
      frame.blackPeriods,
    ),
    white: initializeSide(
      frame.mainTimeMs,
      frame.byoyomiConfigured,
      frame.whiteMainRemainingMs,
      frame.whiteByoyomi,
      frame.whitePeriods,
    ),
    activeTurnKey:
      frame.perspectiveColor === null || frame.activeColor !== frame.perspectiveColor
        ? null
        : `${frame.gameId}:${frame.perspectiveColor}:${frame.turnStartedAt ?? 'unknown'}`,
    lastCountdownSecond: countdownFor(
      activeProjection,
      frame.byoyomiConfigured ? null : activeMainRemainingMs,
    ),
  };
}

function initializeSide(
  mainTimeMs: number | null,
  byoyomiConfigured: boolean,
  mainRemainingMs: number | null,
  byoyomi: ProjectedByoyomi | null,
  periods: number | null,
): SideVoiceMemory {
  return {
    byoyomiEntered: byoyomi !== null,
    lowestPeriods: periods,
    lastMainRemainingMs: mainRemainingMs,
    fiveMinutesAnnounced: milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, FIVE_MINUTES_MS),
    oneMinuteAnnounced: milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, ONE_MINUTE_MS),
    thirtySecondsAnnounced:
      byoyomiConfigured || milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, THIRTY_SECONDS_MS),
    twentySecondsAnnounced:
      byoyomiConfigured || milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, TWENTY_SECONDS_MS),
  };
}

function advanceSide(
  previous: SideVoiceMemory,
  mainTimeMs: number | null,
  byoyomiConfigured: boolean,
  mainRemainingMs: number | null,
  byoyomi: ProjectedByoyomi | null,
  observedPeriods: number | null,
  announcements: string[],
): SideVoiceMemory {
  const crossedFiveMinutes = crossedMilestone(
    previous.fiveMinutesAnnounced,
    previous.lastMainRemainingMs,
    mainRemainingMs,
    mainTimeMs,
    FIVE_MINUTES_MS,
  );
  const crossedOneMinute = crossedMilestone(
    previous.oneMinuteAnnounced,
    previous.lastMainRemainingMs,
    mainRemainingMs,
    mainTimeMs,
    ONE_MINUTE_MS,
  );
  const crossedThirtySeconds =
    !byoyomiConfigured &&
    crossedMilestone(
      previous.thirtySecondsAnnounced,
      previous.lastMainRemainingMs,
      mainRemainingMs,
      mainTimeMs,
      THIRTY_SECONDS_MS,
    );
  const crossedTwentySeconds =
    !byoyomiConfigured &&
    crossedMilestone(
      previous.twentySecondsAnnounced,
      previous.lastMainRemainingMs,
      mainRemainingMs,
      mainTimeMs,
      TWENTY_SECONDS_MS,
    );
  if (crossedFiveMinutes) announcements.push('还剩5分钟');
  if (crossedOneMinute) announcements.push('还剩1分钟');
  if (crossedThirtySeconds) announcements.push('还剩30秒');
  if (crossedTwentySeconds) announcements.push('还剩20秒');

  if (!previous.byoyomiEntered && byoyomi !== null) announcements.push('开始读秒');
  if (
    previous.lowestPeriods !== null &&
    observedPeriods !== null &&
    observedPeriods < previous.lowestPeriods &&
    observedPeriods > 0
  ) {
    announcements.push(`还剩${observedPeriods}次读秒`);
  }
  return {
    byoyomiEntered: previous.byoyomiEntered || byoyomi !== null,
    lowestPeriods:
      observedPeriods === null
        ? previous.lowestPeriods
        : previous.lowestPeriods === null
          ? observedPeriods
          : Math.min(previous.lowestPeriods, observedPeriods),
    lastMainRemainingMs: mainRemainingMs,
    fiveMinutesAnnounced:
      previous.fiveMinutesAnnounced ||
      milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, FIVE_MINUTES_MS),
    oneMinuteAnnounced:
      previous.oneMinuteAnnounced ||
      milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, ONE_MINUTE_MS),
    thirtySecondsAnnounced:
      previous.thirtySecondsAnnounced ||
      byoyomiConfigured ||
      milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, THIRTY_SECONDS_MS),
    twentySecondsAnnounced:
      previous.twentySecondsAnnounced ||
      byoyomiConfigured ||
      milestoneAlreadyPassed(mainTimeMs, mainRemainingMs, TWENTY_SECONDS_MS),
  };
}

function crossedMilestone(
  alreadyAnnounced: boolean,
  previousRemainingMs: number | null,
  currentRemainingMs: number | null,
  mainTimeMs: number | null,
  thresholdMs: number,
): boolean {
  return (
    !alreadyAnnounced &&
    mainTimeMs !== null &&
    mainTimeMs >= thresholdMs &&
    previousRemainingMs !== null &&
    previousRemainingMs >= thresholdMs &&
    currentRemainingMs !== null &&
    currentRemainingMs < thresholdMs &&
    currentRemainingMs >= thresholdMs - MILESTONE_LATE_TOLERANCE_MS
  );
}

function milestoneAlreadyPassed(
  mainTimeMs: number | null,
  mainRemainingMs: number | null,
  thresholdMs: number,
): boolean {
  return mainTimeMs === null || mainTimeMs < thresholdMs || mainRemainingMs === null
    ? true
    : mainRemainingMs < thresholdMs;
}

function countdownFor(
  projection: ProjectedByoyomi | null,
  mainRemainingMs: number | null = null,
): number | null {
  if (projection !== null && projection.periods === 0) return null;
  const remainingMs = projection?.periodRemainingMs ?? mainRemainingMs;
  if (remainingMs === null) return null;
  const seconds = Math.ceil(remainingMs / 1_000);
  return seconds >= 1 && seconds <= 10 ? seconds : null;
}
